import { useEffect, useState } from 'react';
import { Typography, Stack, CircularProgress } from '@mui/material';
import NavLink from '@/components/navigation/NavLink';
import AXIOS from '@/util/axios';
import { Order } from '@/types/types';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '@/util/dateUtils';

function getStatusLabel(status: string | null) {
  return (
    {
      paid: 'Zapłacone',
      completed: 'Zakończone',
      cancelled: 'Anulowane',
    }[status ?? ''] || 'Nowe'
  );
}

function getStatusColor(status: string | null) {
  return (
    {
      new: '#f7c948',
      paid: '#f7c948',
      completed: '#3aaf76',
      cancelled: '#e53935',
    }[status ?? ''] || '#acacac'
  );
}

function getBorderColor(status: string | null) {
  return (
    {
      new: '#d9a436',
      paid: '#d9a436',
      completed: '#2a8957',
      cancelled: '#b71c1c',
    }[status ?? ''] || '#555454'
  );
}

export default function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { authState } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authState?.token) {
      navigate('/user-actions/login', {
        state: {
          redirectTo: location.pathname,
          title: 'Zaloguj się',
          description: 'Musisz się zalogować, aby zobaczyć swoje zamówienia.',
          variant: 'default',
        },
      });
      return;
    }

    async function getOrderData() {
      try {
        const response = await AXIOS.get(`/api/orders/orders`);

        if (response.data && response.data._embedded?.items) {
          setOrders(response.data._embedded.items);
        } else {
          console.error('Unexpected response structure:', response.data);
          setError('Nie udało się załadować danych zamówienia.');
        }
      } catch (error) {
        console.error('Error fetching order data:', error);
        setError('Wystąpił błąd podczas pobierania danych zamówienia.');
      } finally {
        setIsLoading(false);
      }
    }

    getOrderData();
  }, [authState, navigate]);

  if (isLoading) {
    return (
      <div className='flex justify-center items-center h-full'>
        <Stack className='text-gray-500'>
          <CircularProgress color='inherit' />
        </Stack>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex justify-center items-center h-full'>
        <Typography variant='h6'>{error}</Typography>
      </div>
    );
  }

  return (
    <main className='bg-white min-h-[100vh] p-4 md:p-20 flex justify-center'>
      <div className='max-w-[1400px] w-full'>
        <Typography variant='h4' gutterBottom>
          Twoje zamówienia:
        </Typography>
        <section>
          {orders.length > 0 ? (
            orders.map((order: Order) => (
              <div
                key={order.id}
                className='border-b border-[#ff7a4e] py-4 flex flex-col gap-2 relative max-w-[1600px]'
              >
                <div className='flex flex-col md:flex-row justify-between items-start md:items-center'>
                  <div className='mb-4 md:mb-0'>
                    <Typography variant='body1'>
                      Zamówienie numer {order.id}
                    </Typography>
                    <Typography variant='body2'>
                      Data złożenia zamówienia:{' '}
                      {formatDate(order.paymentTimestamp)}
                    </Typography>
                  </div>
                  <div className='flex gap-4 overflow-x-auto justify-center mb-4 md:mb-0'>
                    {order.items && Array.isArray(order.items) ? (
                      order.items.map((item) => (
                        <div
                          key={item.course.id}
                          className='flex items-center justify-center w-32 md:w-40'
                        >
                          <img
                            src={item.course.thumbnailUrl}
                            alt={item.course.name || 'Nieznany kurs'}
                            className='w-full h-auto rounded'
                          />
                        </div>
                      ))
                    ) : (
                      <Typography variant='body2'>
                        Brak kursów w zamówieniu.
                      </Typography>
                    )}
                  </div>
                  <div className='flex flex-col items-start md:items-end justify-center'>
                    <div className='flex justify-center items-center mb-2 md:mb-0'>
                      <Typography
                        variant='body1'
                        gutterBottom
                        style={{ color: '#333' }}
                      >
                        Status zamówienia:
                      </Typography>
                      <div
                        className='px-2 py-1 text-zinc-700 font-semibold text-xs rounded-md shadow-sm text-center mb-1 ml-1'
                        style={{
                          backgroundColor: getStatusColor(order.status),
                          border: `2px solid ${getBorderColor(order.status)}`,
                        }}
                      >
                        {getStatusLabel(order.status)}
                      </div>
                    </div>

                    <Typography variant='h6' style={{ fontWeight: 'bold' }}>
                      KWOTA ŁĄCZNA: {order.totalPriceGross || 'Brak danych'}
                    </Typography>
                  </div>
                </div>
                <NavLink
                  href={`/orders/${order.id}`}
                  title='Szczegóły zamówienia'
                  className='w-fit px-5 py-2 bg-[#ff7a4e] text-white font-medium text-sm rounded-md shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ff7a4e] transition-colors duration-200 ease-in-out'
                />
              </div>
            ))
          ) : (
            <Typography variant='h6'>Brak zamówień do wyświetlenia.</Typography>
          )}
        </section>
      </div>
    </main>
  );
}
